import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { toast } from "sonner";
import { Shield, LogOut, KeyRound, Mail } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminProfile() {
  const { user, logout, changePassword } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (nw !== confirm) { toast.error("New passwords do not match."); return; }
    setLoading(true);
    try {
      await changePassword(cur, nw);
      toast.success("Password updated successfully.");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div data-testid="admin-profile" className="max-w-2xl">
      <h1 className="font-head font-bold text-3xl uc mb-1">Profile &amp; Security</h1>
      <p className="text-gray-500 mb-8">Manage your newsroom admin account.</p>

      <div className="bg-white border border-gray-200 p-6 mb-6">
        <h2 className="font-head font-bold uc text-sm text-gray-500 mb-4 flex items-center gap-2"><Shield size={16} /> Account</h2>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3"><Mail size={16} className="text-gray-400" /><span className="text-gray-500 w-28">Email</span><span className="font-semibold" data-testid="profile-email">{user?.email}</span></div>
          <div className="flex items-center gap-3"><Shield size={16} className="text-gray-400" /><span className="text-gray-500 w-28">Name</span><span className="font-semibold">{user?.name || "—"}</span></div>
          <div className="flex items-center gap-3"><KeyRound size={16} className="text-gray-400" /><span className="text-gray-500 w-28">Role</span><span className="font-semibold uppercase">{user?.role || "admin"}</span></div>
          <div className="flex items-center gap-3"><Shield size={16} className="text-gray-400" /><span className="text-gray-500 w-28">Session</span><span className="font-semibold">JWT Bearer · expires in 7 days · auto sign-out on expiry</span></div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-6 mb-6">
        <h2 className="font-head font-bold uc text-sm text-gray-500 mb-4 flex items-center gap-2"><KeyRound size={16} /> Change Password</h2>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <Field label="Current Password" value={cur} onChange={setCur} testid="profile-current-password" />
          <Field label="New Password (min 8 chars)" value={nw} onChange={setNw} testid="profile-new-password" />
          <Field label="Confirm New Password" value={confirm} onChange={setConfirm} testid="profile-confirm-password" />
          <button disabled={loading} data-testid="profile-change-password-submit" className="bg-r24-red text-white font-bold uc text-sm px-6 py-3 hover:bg-r24-reddark disabled:opacity-50">
            {loading ? "Updating…" : "Update Password"}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-4">If you ever forget your password, it can be reset securely from the server environment (ADMIN_PASSWORD secret). Passwords are never stored in plain text or shown anywhere.</p>
      </div>

      <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="profile-signout"
        className="flex items-center gap-2 border-2 border-black font-bold uc text-sm px-6 py-3 hover:bg-black hover:text-white">
        <LogOut size={16} /> Sign Out
      </button>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-head font-semibold uc text-xs">{label}</label>
      <input type="password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1" />
    </div>
  );
}
